import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Heading from '@tiptap/extension-heading';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { BusinessPlan, BusinessPlanSection, SectionId, SectionStatus, SECTION_METADATA, GenerationContext, SectionIdEnum } from '../../types/businessPlan';
import { PageBreak } from '../../extensions/PageBreak';
import ErrorBoundary from '../common/ErrorBoundary';
import { AnthropicService } from '../../services/anthropic/anthropicService';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import './BusinessPlanDocument.css';
import FinancialCharts from './charts/FinancialCharts';
import MarketAnalysisCharts from './charts/MarketAnalysisCharts';
import { extractFinancialData, extractMarketData } from './utils/dataExtractor';
import BusinessPlanSectionEditor from './BusinessPlanSectionEditor';

interface BusinessPlanDocumentProps {
  plan: BusinessPlan;
  loading: boolean;
  error: string | null;
  refreshPlan: () => void;
  updateBusinessPlan?: (updatedPlan: Partial<BusinessPlan>) => Promise<BusinessPlan | null>;
  ideaId?: string;
  onEditSection?: (sectionId: SectionId, content: string) => Promise<boolean>;
}

const BusinessPlanDocument: React.FC<BusinessPlanDocumentProps> = ({
  plan,
  loading,
  error: initialError,
  refreshPlan,
  updateBusinessPlan,
  ideaId,
  onEditSection
}) => {
  const [error, setError] = useState<string | null>(initialError);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [fullContent, setFullContent] = useState<string>('');
  const documentRef = useRef<HTMLDivElement>(null);
  const [editingSection, setEditingSection] = useState<SectionId | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Heading.configure({
        levels: [1, 2, 3, 4, 5, 6],
      }),
      Document,
      Paragraph,
      Text,
      PageBreak,
    ],
    content: '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setFullContent(html);
    },
  });

  // Extract financial and market data for charts
  const financialData = useMemo(() => {
    if (!plan) return null;
    return extractFinancialData(plan);
  }, [plan]);

  const marketData = useMemo(() => {
    if (!plan) return null;
    return extractMarketData(plan);
  }, [plan]);

  // Generate the full document content from the business plan
  const generateDocumentContent = useMemo(() => {
    if (!plan) return '';
    
    // Log the plan data to debug
    console.log('Generating content from plan:', plan);
    console.log('Plan sections:', plan.sections);
    console.log('Plan content:', plan.content);
    
    let content = '';
    
    // Cover page
    content += `<div class="cover-page">`;
    content += `<h1 style="text-align: center; margin-top: 4rem;">${plan.title || 'Business Plan'}</h1>`;
    content += `<h3 style="text-align: center; margin-top: 1rem;">Business Plan</h3>`;
    content += `<p style="text-align: center; margin-top: 2rem;">${new Date().toLocaleDateString()}</p>`;
    content += `</div>`;
    content += `<div class="page-break"></div>`;
    
    // Table of Contents
    content += `<div class="toc-container">`;
    content += `<h2>Table of Contents</h2>`;
    content += `<div class="toc">`;
    
    // Add TOC entries for each section
    Object.entries(SECTION_METADATA).forEach(([sectionId, metadata], index) => {
      // Skip cover page and table of contents in the TOC
      if (sectionId === 'cover-page' || sectionId === 'table-of-contents') return;
      content += `<div class="toc-item"><span>${index - 1}. ${metadata.title}</span><span class="toc-dots"></span><span class="toc-page">${metadata.pageNumber}</span></div>`;
    });
    
    content += `</div>`;
    content += `</div>`;
    content += `<div class="page-break"></div>`;
    
    // Add each section with its content
    Object.entries(SECTION_METADATA).forEach(([sectionId, metadata], index) => {
      // Skip cover page and table of contents as sections
      if (sectionId === 'cover-page' || sectionId === 'table-of-contents') return;
      
      const typedSectionId = sectionId as SectionId;
      let sectionContent = '';
      
      // THOROUGH DEBUGGING - Log all potential places content could be
      console.log(`Checking section ${sectionId}:`);
      console.log(`- sections object:`, plan.sections?.[typedSectionId]);
      console.log(`- content object:`, plan.content?.[typedSectionId.replace(/-/g, '_') as keyof typeof plan.content]);
      console.log(`- direct property:`, (plan as any)[typedSectionId.replace(/-/g, '_')]);
      
      // Try to get content from the sections object first
      if (plan.sections && plan.sections[typedSectionId] && plan.sections[typedSectionId].content) {
        sectionContent = plan.sections[typedSectionId].content;
        console.log(`Found content in sections for ${sectionId}, length: ${sectionContent.length}`);
        console.log(`Preview: ${sectionContent.substring(0, 100)}...`);
      } 
      // Fall back to the content object if available
      else if (plan.content) {
        // Map section IDs to content object keys
        const contentKey = typedSectionId.replace(/-/g, '_') as keyof typeof plan.content;
        if (plan.content[contentKey]) {
          sectionContent = plan.content[contentKey] as string;
          console.log(`Found content in plan.content for ${sectionId}, length: ${sectionContent.length}`);
          console.log(`Preview: ${sectionContent.substring(0, 100)}...`);
        }
      }
      
      // If we still don't have content, check if it exists directly on the plan
      if (!sectionContent && typedSectionId.replace(/-/g, '_') in plan) {
        const directKey = typedSectionId.replace(/-/g, '_') as keyof BusinessPlan;
        const directContent = plan[directKey];
        if (typeof directContent === 'string') {
          sectionContent = directContent;
          console.log(`Found content directly on plan for ${sectionId}, length: ${sectionContent.length}`);
          console.log(`Preview: ${sectionContent.substring(0, 100)}...`);
        }
      }
      
      // If content is missing, check the status to give appropriate feedback
      if (!sectionContent && plan.sections?.[typedSectionId]) {
        const status = plan.sections[typedSectionId].status;
        console.log(`No content found for ${sectionId}, status: ${status}`);
        
        if (status === 'generating') {
          sectionContent = `<p class="generating-message">Generating content for ${metadata.title}... Please wait.</p>`;
        } else if (status === 'error') {
          sectionContent = `<p class="error-message">Error generating content for ${metadata.title}. Please try regenerating this section.</p>`;
        } else {
          sectionContent = `<p class="no-content-message">No content available for ${metadata.title}. This section needs to be generated.</p>`;
        }
      }
      
      content += `<div class="section" id="section-${sectionId}">`;
      content += `<div class="section-header">`;
      content += `<span class="section-number">${index - 1}</span>`;
      content += `<h2>${metadata.title}</h2>`;
      if (onEditSection) {
        content += `<button class="edit-section-btn" data-section-id="${sectionId}" onclick="document.dispatchEvent(new CustomEvent('editSection', { detail: { sectionId: '${sectionId}' } }));">Edit</button>`;
      }
      content += `</div>`;
      
      content += `<div class="section-content">`;
      // Always use sectionContent, which now has appropriate fallbacks
      content += sectionContent || `<p>No content available for this section.</p>`;
      content += `</div>`;
      
      content += `</div>`;
      content += `<div class="page-break"></div>`;
    });
    
    return content;
  }, [plan, onEditSection]);

  // Initialize editor with content when plan changes
  useEffect(() => {
    if (editor && plan) {
      const generatedContent = generateDocumentContent;
      // Only update editor if fullContent doesn't match the generated content
      // This prevents unnecessary editor updates that can cause infinite loops
      if (generatedContent !== fullContent) {
        console.log('Updating editor with new content');
        editor.commands.setContent(generatedContent);
        setFullContent(generatedContent);
      }
    }
  }, [plan, editor, generateDocumentContent, fullContent]);

  // Save the full document content
  const handleSaveDocument = async () => {
    if (!plan || !fullContent) return;
    
    try {
      setIsSaving(true);
      
      // Extract section content from the editor HTML
      // This is a simplified approach - in a real implementation, we would parse the HTML more carefully
      const parser = new DOMParser();
      const doc = parser.parseFromString(fullContent, 'text/html');
      
      // Create updated sections with content
      const updatedSections: Record<SectionId, BusinessPlanSection> = { ...plan.sections };
      
      // For each section in the document, update its content
      Object.keys(SECTION_METADATA).forEach((sectionId) => {
        if (sectionId === 'cover-page' || sectionId === 'table-of-contents') return;
        
        const typedSectionId = sectionId as SectionId;
        const sectionElement = doc.getElementById(`section-${sectionId}`);
        
        if (sectionElement) {
          // Get the content div (should be the second child after the h2)
          const contentDiv = sectionElement.querySelector('.section-content');
          const sectionContent = contentDiv ? contentDiv.innerHTML : '';
          
          // Update the section content
          if (updatedSections[typedSectionId]) {
            updatedSections[typedSectionId] = {
              ...updatedSections[typedSectionId],
              content: sectionContent
            };
          } else {
            // Create a new section if it doesn't exist
            updatedSections[typedSectionId] = {
              id: typedSectionId,
              title: SECTION_METADATA[typedSectionId].title,
              content: sectionContent,
              status: 'completed' as SectionStatus,
              pageNumber: SECTION_METADATA[typedSectionId].pageNumber
            };
          }
        }
      });
      
      // Create a simplified structure for the business plan
      const updatedPlan: Partial<BusinessPlan> = {
        ...plan,
        sections: updatedSections,
        // Also update the content object for backward compatibility
        content: {
          executive_summary: updatedSections['executive-summary']?.content || '',
          market_analysis: updatedSections['market-research']?.content || '',
          organization: updatedSections['operations']?.content || '',
          financial_plan: updatedSections['financials']?.content || '',
          marketing_strategy: updatedSections['sales-marketing']?.content || ''
        }
      };
      
      if (updateBusinessPlan) {
        await updateBusinessPlan(updatedPlan);
      }
      
      setIsSaving(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setIsSaving(false);
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      console.error('Error saving document:', errorMessage);
      setError(errorMessage);
    }
  };

  // Save the full document content
  const handleSaveToSupabase = async () => {
    if (!plan) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      console.log('Saving to Supabase, current plan:', plan);
      
      // Check if we have content in the sections
      const hasContent = plan.sections && Object.values(plan.sections).some(section => 
        section && section.content && section.content.length > 10
      );

      // If content exists, skip regeneration
      if (hasContent) {
        console.log('Existing content found, skipping regeneration.');
        // Proceed to save the data to Supabase
        const updatedPlan = { ...plan, sections: plan.sections };
        if (updateBusinessPlan) {
          await updateBusinessPlan(updatedPlan);
        }
        setIsSaving(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        return;
      }
      
      console.log('Has content:', hasContent);
      
      // If no content, we need to generate it first
      if (!hasContent && plan.businessIdea) {
        console.log('No content found, attempting to generate content...');
        
        // Create a context for generation
        const context: GenerationContext = {
          businessIdea: plan.businessIdea,
          currentSection: 'executive-summary' as SectionId,
          sections: plan.sections || {}
        };
        
        if (plan.additionalData) {
          context.additionalData = plan.additionalData;
        }
        
        // Create anthropic service
        const anthropicService = new AnthropicService();
        
        // Generate content for each section
        const updatedSections: Record<SectionId, BusinessPlanSection> = { ...plan.sections };
        const sectionIds = Object.keys(SECTION_METADATA).filter(id => 
          id !== 'cover-page' && id !== 'table-of-contents'
        ) as SectionId[];
        
        for (const sectionId of sectionIds) {
          try {
            console.log(`Generating content for section ${sectionId}...`);
            const regeneratedSection = await anthropicService.regenerateSection(sectionId, context);
            updatedSections[sectionId] = regeneratedSection;
            
            // Update the context with the new section
            context.sections = updatedSections;
          } catch (err) {
            console.error(`Error generating section ${sectionId}:`, err);
            // Create a placeholder for the failed section
            updatedSections[sectionId] = {
              id: sectionId,
              title: SECTION_METADATA[sectionId].title,
              content: `<p>Failed to generate content for ${SECTION_METADATA[sectionId].title}. Please try again.</p>`,
              status: 'error',
              pageNumber: SECTION_METADATA[sectionId].pageNumber
            };
          }
        }
        
        // Create a simplified structure for the business plan
        const updatedPlan: Partial<BusinessPlan> = {
          ...plan,
          sections: updatedSections,
          status: 'completed',
          // Use ideaId prop if available
          idea_id: ideaId || plan.idea_id || plan.businessIdea?.id,
          // Also update the content object for backward compatibility
          content: {
            executive_summary: updatedSections['executive-summary']?.content || '',
            market_analysis: updatedSections['market-research']?.content || '',
            organization: updatedSections['operations']?.content || '',
            financial_plan: updatedSections['financials']?.content || '',
            marketing_strategy: updatedSections['sales-marketing']?.content || ''
          }
        };
        
        // Save the updated plan
        if (updateBusinessPlan) {
          const savedPlan = await updateBusinessPlan(updatedPlan);
          if (savedPlan) {
            // After saving, refresh the plan via the parent component to update all UI
            refreshPlan();
          }
        }
      }
      
      setIsSaving(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setIsSaving(false);
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      console.error('Error saving document:', errorMessage);
      setError(errorMessage);
    }
  };

  // Function to handle refresh (must be in component scope)
  const handleRefresh = () => {
    refreshPlan();
  };

  // Function to handle edit button click
  const handleEditClick = (sectionId: SectionId) => {
    setEditingSection(sectionId);
    setIsEditing(true);
  };
  
  // Function to handle canceling edit
  const handleCancelEdit = () => {
    setEditingSection(null);
    setIsEditing(false);
  };
  
  // Function to handle saving edited content
  const handleSaveEdit = async (sectionId: SectionId, content: string) => {
    if (onEditSection) {
      const success = await onEditSection(sectionId, content);
      if (success) {
        setEditingSection(null);
        setIsEditing(false);
      }
      return success;
    }
    return false;
  };

  // Handle editing section
  useEffect(() => {
    // Add event listener for the edit section button clicks (from HTML content)
    const handleEditSectionEvent = (event: CustomEvent<{sectionId: SectionId}>) => {
      const { sectionId } = event.detail;
      handleEditClick(sectionId);
    };

    document.addEventListener('editSection', handleEditSectionEvent as EventListener);
    
    return () => {
      document.removeEventListener('editSection', handleEditSectionEvent as EventListener);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p>No business plan found. Please create a new business plan.</p>
            <button 
              onClick={handleRefresh}
              className="text-sm text-red-700 hover:text-red-900 underline mt-1"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render the editor or the document
  if (isEditing && editingSection && onEditSection) {
    const section = plan.sections?.[editingSection] || {
      id: editingSection,
      title: SECTION_METADATA[editingSection]?.title || 'Section',
      content: '',
      status: 'not-started',
      pageNumber: 0
    };
    
    return (
      <BusinessPlanSectionEditor
        section={section}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        isLoading={loading}
      />
    );
  }

  return (
    <ErrorBoundary>
      <div className="business-plan-document">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        ) : (
          <div className="business-plan">
            {/* Document actions */}
            <div className="flex justify-end space-x-2 mb-4">
              <button
                onClick={handleSaveDocument}
                disabled={isSaving}
                className={`px-4 py-2 rounded ${saveSuccess ? 'bg-green-500' : 'bg-blue-500'} text-white`}
              >
                {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Document'}
              </button>
              <button
                onClick={handleSaveToSupabase}
                disabled={isSaving}
                className="px-4 py-2 rounded bg-blue-500 text-white"
              >
                {isSaving ? 'Saving...' : 'Save to Supabase'}
              </button>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 rounded bg-gray-500 text-white"
              >
                Refresh
              </button>
            </div>
            
            {/* Document content */}
            <div ref={documentRef} id="business-plan-document" className="bg-white rounded-lg shadow-md p-6">
              {/* Cover page */}
              <div className="cover-page">
                <h1 className="text-center">{plan.title || 'Business Plan'}</h1>
                <h3 className="text-center mt-4">Business Plan</h3>
                <p className="text-center mt-8">{new Date().toLocaleDateString()}</p>
              </div>
              
              <div className="page-break"></div>
              
              {/* Table of Contents */}
              <div className="toc-container">
                <h2>Table of Contents</h2>
                <div className="toc">
                  {Object.entries(SECTION_METADATA)
                    .filter(([sectionId]) => sectionId !== 'cover-page' && sectionId !== 'table-of-contents')
                    .map(([sectionId, metadata], index) => (
                      <div key={sectionId} className="toc-item">
                        <span>{index + 1}. {metadata.title}</span>
                        <span className="toc-dots"></span>
                        <span className="toc-page">{metadata.pageNumber}</span>
                      </div>
                    ))}
                </div>
              </div>
              
              <div className="page-break"></div>
              
              {/* Sections */}
              {Object.entries(SECTION_METADATA)
                .filter(([sectionId]) => sectionId !== 'cover-page' && sectionId !== 'table-of-contents')
                .map(([sectionId, metadata], index) => {
                  const typedSectionId = sectionId as SectionId;
                  const section = plan.sections?.[typedSectionId];
                  const sectionContent = section?.content || '';
                  
                  return (
                    <div key={sectionId} className="section" id={`section-${sectionId}`}>
                      <div className="section-header">
                        <span className="section-number">{index + 1}</span>
                        <h2>{metadata.title}</h2>
                        {onEditSection && (
                          <button 
                            className="edit-section-btn" 
                            onClick={() => handleEditClick(typedSectionId)}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                      <div className="section-content">
                        {sectionContent ? (
                          <ReactMarkdown remarkPlugins={[gfm]}>
                            {sectionContent}
                          </ReactMarkdown>
                        ) : (
                          <p>No content available for this section.</p>
                        )}
                        
                        {/* Add financial charts to the financials section */}
                        {typedSectionId === SectionIdEnum.FINANCIALS && financialData && (
                          <div className="mt-8 financial-charts">
                            <h3 className="text-xl font-semibold mb-4">Financial Projections</h3>
                            <FinancialCharts data={financialData} />
                          </div>
                        )}
                        
                        {/* Add market analysis charts to the market research section */}
                        {typedSectionId === SectionIdEnum.MARKET_RESEARCH && marketData && (
                          <div className="mt-8 market-analysis-charts">
                            <h3 className="text-xl font-semibold mb-4">Market Analysis</h3>
                            <MarketAnalysisCharts 
                              data={marketData}
                            />
                          </div>
                        )}
                      </div>
                      <div className="page-break"></div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default BusinessPlanDocument;
